import React, { useState, useMemo } from "react";
import axios from "axios";
import {
  Sparkles,
  Gauge,
  Building2,
  Mountain,
  Volume,
  Zap,
  PiggyBank,
  Scale,
  Trophy,
  Car,
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";
import TireImage from "./TireImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Generic tire silhouette shown for aspirational (out-of-catalog) picks. */
const AspirationalImage = ({ brand, model }) => (
  <div
    className="w-full h-full flex items-center justify-center"
    title={`${brand} ${model}`}
    aria-label={`${brand} ${model}`}
  >
    <svg
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
      className="w-20 h-20 md:w-32 md:h-32 drop-shadow-[0_15px_30px_rgba(0,0,0,0.5)]"
      aria-hidden="true"
    >
      <circle cx="100" cy="100" r="95" fill="#111827" stroke="#0b1220" strokeWidth="2" />
      {Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * 15 * Math.PI) / 180;
        const x1 = 100 + Math.cos(angle) * 78;
        const y1 = 100 + Math.sin(angle) * 78;
        const x2 = 100 + Math.cos(angle) * 92;
        const y2 = 100 + Math.sin(angle) * 92;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#374151" strokeWidth="4" />;
      })}
      <circle cx="100" cy="100" r="55" fill="#1f2937" stroke="#374151" strokeWidth="2" />
      {Array.from({ length: 5 }).map((_, i) => {
        const angle = ((i * 72 - 90) * Math.PI) / 180;
        const x = 100 + Math.cos(angle) * 48;
        const y = 100 + Math.sin(angle) * 48;
        return (
          <line
            key={i}
            x1="100"
            y1="100"
            x2={x}
            y2={y}
            stroke="#4b5563"
            strokeWidth="6"
            strokeLinecap="round"
          />
        );
      })}
      <circle cx="100" cy="100" r="12" fill="#FF5A00" opacity="0.9" />
      <circle cx="100" cy="100" r="5" fill="#0b1220" />
    </svg>
  </div>
);

/**
 * Tinder dos Pneus — short interactive quiz that scores a pool of tires
 * against the user's profile and reveals the best match at the end.
 */

const QUESTIONS = [
  {
    id: "km",
    label: "Quantos km faz por ano?",
    options: [
      { value: "very-low", label: "Menos de 5.000 km", sub: "Fim-de-semana, 2º carro", icon: Building2 },
      { value: "low", label: "5.000 a 12.000 km", sub: "Uso ocasional, citadino", icon: Building2 },
      { value: "mid", label: "12.000 a 20.000 km", sub: "Uso regular, misto", icon: Gauge },
      { value: "high", label: "20.000 a 35.000 km", sub: "Comutas longas, viagens", icon: Mountain },
      { value: "very-high", label: "Mais de 35.000 km", sub: "Profissional, frota", icon: Mountain },
    ],
  },
  {
    id: "tipo",
    label: "Onde conduz mais?",
    options: [
      { value: "cidade", label: "Cidade", sub: "Trânsito, semáforos, baixa velocidade", icon: Building2 },
      { value: "misto", label: "Misto", sub: "Um pouco de tudo", icon: Gauge },
      { value: "estrada", label: "Autoestrada", sub: "Velocidade de cruzeiro, distância", icon: Mountain },
    ],
  },
  {
    id: "estilo",
    label: "O que privilegia ao conduzir?",
    options: [
      { value: "silencio", label: "Silêncio e conforto", sub: "Quero ouvir música em paz", icon: Volume },
      { value: "equilibrio", label: "Equilíbrio entre os dois", sub: "Conforto sem perder dinâmica", icon: Scale },
      { value: "desporto", label: "Aderência desportiva", sub: "Direção incisiva, performance", icon: Zap },
    ],
  },
  {
    id: "orcamento",
    label: "Qual o seu orçamento?",
    options: [
      { value: "economico", label: "Económico", sub: "Qualidade ao melhor preço", icon: PiggyBank },
      { value: "equilibrado", label: "Equilibrado", sub: "Pago para ter qualidade", icon: Scale },
      { value: "performance", label: "Performance", sub: "O melhor que houver", icon: Trophy },
    ],
  },
  {
    id: "carro",
    label: "Qual a marca do seu carro?",
    options: [
      {
        value: "premium-de",
        label: "Premium alemã",
        sub: "BMW · Audi · Mercedes · Porsche",
        icon: Car,
      },
      {
        value: "premium-outra",
        label: "Premium internacional",
        sub: "Volvo · Lexus · Tesla · Mini · Cupra · Jaguar · Land Rover",
        icon: Car,
      },
      {
        value: "mainstream",
        label: "Generalista",
        sub: "VW · Toyota · Honda · Mazda · SEAT · Skoda · Hyundai · Kia · Nissan",
        icon: Car,
      },
      {
        value: "comum",
        label: "Citadino / Familiar",
        sub: "Renault · Peugeot · Citroën · Ford · Opel · Fiat · Dacia · outras",
        icon: Car,
      },
    ],
  },
];

// Tier classification for each tire in the pool. Used by the car-brand match
// algorithm: when the user picks a brand segment, tires of the matching tier
// get a score bonus so we recommend pneus that fit the typical purchase
// profile of that vehicle category.
const TIRE_TIER = {
  "sailun-atrezzo-elite2": "economy",
  "sailun-atrezzo-zsr": "economy",
  "laufenn-gfit-eq-plus": "economy",
  "laufenn-sfit-eq": "economy",
  "taurus-summer3": "economy",
  "taurus-high-performance": "economy",
  "goodyear-efficientgrip-performance-2": "mainstream",
  "goodyear-eagle-f1-asymmetric-6": "performance",
  "hankook-kinergy-eco-2": "mainstream",
  "hankook-ventus-prime4": "mainstream",
  "hankook-ventus-evo": "performance",
  "bridgestone-turanza-t005": "mainstream",
  "bridgestone-potenza-sport": "performance",
  "continental-premiumcontact-7": "premium",
  "continental-sportcontact-7": "performance",
  "pirelli-cinturato-p7": "premium",
  "pirelli-p-zero-pz4": "performance",
  "michelin-primacy-5": "premium",
  "michelin-pilot-sport-5": "performance",
};

// Car-brand segment → ordered list of preferred tire tiers.
// First tier in the list gets the bigger bonus, second a smaller one.
const CAR_PREFERENCE = {
  "premium-de": ["performance", "premium"],
  "premium-outra": ["premium", "performance"],
  mainstream: ["mainstream", "premium"],
  comum: ["economy", "mainstream"],
};

// "Pick of the house" — for each car segment we have a Hankook model the
// workshop specifically recommends (Hankook is the partner brand). The
// matching tire gets a small extra boost so it reliably surfaces in the top
// results without overriding strong matches from other brands when the
// user's profile is very specific.
const HANKOOK_PICK = {
  "premium-de": "hankook-ventus-evo",
  "premium-outra": "hankook-ventus-prime4",
  mainstream: "hankook-ventus-prime4",
  comum: "hankook-kinergy-eco-2",
};

const TIRE_POOL = [
  // ─────────── SAILUN ───────────
  {
    id: "sailun-atrezzo-elite2", brand: "Sailun", model: "Atrezzo Elite 2",
    image: "/tires/sailun-atrezzo-elite2.png",
    tagline: "O equilíbrio inteligente para o uso diário.",
    why: "Silencioso, confortável e com um preço difícil de bater. Perfeito para utilitários e citadinos com quilometragem moderada.",
    score: (a) =>
      (a.orcamento === "economico" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "cidade" ? 3 : a.tipo === "misto" ? 2 : 0) +
      (a.estilo === "silencio" ? 3 : a.estilo === "equilibrio" ? 1 : 0) +
      (a.km === "low" ? 3 : a.km === "mid" ? 1 : 0),
  },
  {
    id: "sailun-atrezzo-zsr", brand: "Sailun", model: "Atrezzo ZSR",
    image: "/tires/sailun-atrezzo-elite2.png",
    tagline: "Performance económica para condução dinâmica.",
    why: "Linha desportiva da Sailun com perfil mais agressivo. Boa travagem em seco a um preço de entrada de gama. Para quem quer sensação desportiva sem ir ao premium.",
    score: (a) =>
      (a.orcamento === "economico" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "misto" ? 3 : a.tipo === "estrada" ? 2 : 0) +
      (a.estilo === "desporto" ? 4 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "low" ? 2 : a.km === "mid" ? 2 : 0),
  },

  // ─────────── LAUFENN ───────────
  {
    id: "laufenn-gfit-eq-plus", brand: "Laufenn", model: "G Fit EQ+ (LK41)",
    image: "/tires/laufenn-gfit-eq-plus.png",
    tagline: "Marca-irmã da Hankook — qualidade coreana ao preço certo.",
    why: "Aderência sólida em molhado e seco com baixo consumo de combustível. Entrada de gama ideal para quem quer fugir das marcas budget chinesas sem ir ao premium.",
    score: (a) =>
      (a.orcamento === "economico" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "cidade" ? 3 : a.tipo === "misto" ? 3 : 1) +
      (a.estilo === "equilibrio" ? 2 : a.estilo === "silencio" ? 2 : 0) +
      (a.km === "low" ? 3 : a.km === "mid" ? 2 : 0),
  },
  {
    id: "laufenn-sfit-eq", brand: "Laufenn", model: "S Fit EQ+ (LK01)",
    image: "/tires/laufenn-gfit-eq-plus.png",
    tagline: "Performance desportiva acessível.",
    why: "Pneu UHP da Laufenn, com ADN Hankook em formato mais económico. Boa travagem em seco e direção firme — uma alternativa inteligente para hatch-backs desportivos.",
    score: (a) =>
      (a.orcamento === "economico" ? 4 : a.orcamento === "equilibrado" ? 3 : 0) +
      (a.tipo === "misto" ? 3 : a.tipo === "estrada" ? 2 : 1) +
      (a.estilo === "desporto" ? 4 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "mid" ? 2 : a.km === "low" ? 2 : 1),
  },

  // ─────────── TAURUS ───────────
  {
    id: "taurus-summer3", brand: "Taurus", model: "Summer 3",
    image: "/tires/taurus-summer3.png",
    tagline: "Fabricado pela Michelin Group — premium sem o preço premium.",
    why: "Aderência fiável em molhado e seco a um preço excelente. A escolha racional para quem quer confiança da Michelin Group sem pagar a etiqueta premium.",
    score: (a) =>
      (a.orcamento === "economico" ? 4 : a.orcamento === "equilibrado" ? 3 : 0) +
      (a.tipo === "misto" ? 3 : a.tipo === "cidade" ? 2 : 1) +
      (a.estilo === "equilibrio" ? 3 : a.estilo === "silencio" ? 1 : 1) +
      (a.km === "mid" ? 3 : a.km === "low" ? 2 : 1),
  },
  {
    id: "taurus-high-performance", brand: "Taurus", model: "High Performance",
    image: "/tires/taurus-summer3.png",
    tagline: "Tecnologia Michelin Group em pneu desportivo económico.",
    why: "Versão UHP da gama Taurus, com flancos mais firmes e composto otimizado para condução dinâmica. Boa entrada para quem quer desporto sem gastar 150€ por pneu.",
    score: (a) =>
      (a.orcamento === "economico" ? 4 : a.orcamento === "equilibrado" ? 3 : 0) +
      (a.tipo === "misto" ? 3 : a.tipo === "estrada" ? 2 : 1) +
      (a.estilo === "desporto" ? 4 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "mid" ? 3 : a.km === "low" ? 1 : 1),
  },

  // ─────────── GOODYEAR ───────────
  {
    id: "goodyear-efficientgrip-performance-2", brand: "Goodyear", model: "EfficientGrip Performance 2",
    image: "/tires/goodyear-efficientgrip-performance-2.png",
    tagline: "Eficiência e durabilidade em pneu americano.",
    why: "Baixa resistência ao rolamento (poupa combustível), longa duração e bom desempenho em molhado. Equipamento de origem em VW, Renault e Mercedes.",
    score: (a) =>
      (a.orcamento === "equilibrado" ? 4 : a.orcamento === "economico" ? 2 : 1) +
      (a.tipo === "misto" ? 3 : a.tipo === "estrada" ? 2 : 2) +
      (a.estilo === "equilibrio" ? 3 : a.estilo === "silencio" ? 2 : 1) +
      (a.km === "mid" ? 3 : a.km === "high" ? 2 : 1),
  },
  {
    id: "goodyear-eagle-f1-asymmetric-6", brand: "Goodyear", model: "Eagle F1 Asymmetric 6",
    image: "/tires/goodyear-eagle-f1-asymmetric-6.png",
    tagline: "Pneu desportivo top da gama Goodyear.",
    why: "Equipamento de origem em modelos Porsche, Audi RS e BMW M. Travagem em seco entre as melhores do mercado e excelente comportamento em curva. Para condução entusiasta.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 3 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 3 : 1) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 3 : 0) +
      (a.km === "mid" ? 3 : a.km === "high" ? 2 : 1),
  },

  // ─────────── HANKOOK ───────────
  {
    id: "hankook-kinergy-eco-2", brand: "Hankook", model: "Kinergy Eco 2 (K435)",
    image: "/tires/hankook-kinergy-eco-2.png",
    tagline: "Eco-condução com qualidade Hankook.",
    why: "Pneu eco da Hankook com baixa resistência ao rolamento (classe B) — reduz consumo até 4%. Silencioso, durável e equipamento de origem em vários híbridos.",
    score: (a) =>
      (a.orcamento === "economico" ? 4 : a.orcamento === "equilibrado" ? 3 : 1) +
      (a.tipo === "cidade" ? 4 : a.tipo === "misto" ? 3 : 1) +
      (a.estilo === "silencio" ? 4 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "low" ? 3 : a.km === "mid" ? 3 : 1),
  },
  {
    id: "hankook-ventus-prime4", brand: "Hankook", model: "Ventus Prime 4 (K135)",
    image: "/tires/hankook-ventus-prime4.png",
    tagline: "Premium coreano, equipamento de origem de BMW e Audi.",
    why: "Excelente travagem em seco e direção incisiva, com tecnologia herdada de fornecedor das maiores marcas premium. A escolha equilibrada entre desporto e durabilidade.",
    score: (a) =>
      (a.orcamento === "equilibrado" ? 5 : a.orcamento === "performance" ? 3 : 1) +
      (a.tipo === "misto" ? 3 : a.tipo === "estrada" ? 3 : 1) +
      (a.estilo === "equilibrio" ? 4 : a.estilo === "desporto" ? 3 : 1) +
      (a.km === "mid" ? 3 : a.km === "high" ? 2 : 1),
  },
  {
    id: "hankook-ventus-evo", brand: "Hankook", model: "Ventus evo (K137)",
    image: "/tires/hankook-ventus-evo.png",
    tagline: "Premium desportivo de nova geração — sucessor do S1 evo³.",
    why: "Modelo mais recente da linha Ventus, lançado em substituição do S1 evo³. Equipamento de origem em BMW, Mercedes e Porsche, com aderência desportiva e tecnologia Sound Absorber para máximo silêncio.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 3 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 3 : 1) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 3 : 1) +
      (a.km === "high" ? 3 : a.km === "mid" ? 2 : 1),
  },

  // ─────────── BRIDGESTONE ───────────
  {
    id: "bridgestone-turanza-t005", brand: "Bridgestone", model: "Turanza T005",
    image: "/tires/bridgestone-turanza-t005.png",
    tagline: "Conforto e longevidade japonesa para autoestrada.",
    why: "Composto NanoPro-Tech para máxima aderência em molhado. Equipamento de origem em Toyota, Lexus, BMW e Mercedes. Ideal para quem faz muitas viagens longas.",
    score: (a) =>
      (a.orcamento === "equilibrado" ? 4 : a.orcamento === "performance" ? 4 : 1) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 3 : 1) +
      (a.estilo === "silencio" ? 4 : a.estilo === "equilibrio" ? 3 : 1) +
      (a.km === "high" ? 3 : a.km === "mid" ? 3 : 1),
  },
  {
    id: "bridgestone-potenza-sport", brand: "Bridgestone", model: "Potenza Sport",
    image: "/tires/bridgestone-potenza-sport.png",
    tagline: "ADN de circuito, certificado para a estrada.",
    why: "Pneu UUHP usado em Ferrari, Lamborghini, Audi RS e Mercedes-AMG de origem. Agarre fenomenal em seco e travagem em molhado de classe A.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 2 : 0) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "mid" ? 2 : a.km === "high" ? 2 : 1),
  },

  // ─────────── CONTINENTAL ───────────
  {
    id: "continental-premiumcontact-7", brand: "Continental", model: "PremiumContact 7",
    image: "/tires/continental-premiumcontact-7.png",
    tagline: "Vencedor de testes ADAC 2024 — referência alemã.",
    why: "Travagem em molhado entre as melhores do mercado e durabilidade excecional. Equipamento de origem em BMW, Audi, Mercedes e Porsche.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 4 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 3 : 2) +
      (a.estilo === "equilibrio" ? 4 : a.estilo === "silencio" ? 4 : 2) +
      (a.km === "high" ? 4 : a.km === "mid" ? 3 : 1),
  },
  {
    id: "continental-sportcontact-7", brand: "Continental", model: "SportContact 7",
    image: "/tires/continental-sportcontact-7.png",
    tagline: "O ultra-desportivo alemão de referência.",
    why: "Pneu UUHP da Continental, equipamento de origem em Porsche 911, BMW M e Audi RS. Aderência em curva e travagem em seco de elite absoluta.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 2 : 0) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "mid" ? 2 : a.km === "high" ? 2 : 1),
  },

  // ─────────── PIRELLI ───────────
  {
    id: "pirelli-cinturato-p7", brand: "Pirelli", model: "Cinturato P7 (P7C2)",
    image: "/tires/pirelli-cinturato-p7.png",
    tagline: "Premium italiano com ADN de Fórmula 1.",
    why: "Aderência desportiva em curva e travagem em seco de elite. Equipamento de origem em Audi RS, BMW M e Porsche.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 2 : 0) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 2 : 0) +
      (a.km === "mid" ? 2 : a.km === "high" ? 2 : 1),
  },
  {
    id: "pirelli-p-zero-pz4", brand: "Pirelli", model: "P Zero (PZ4)",
    image: "/tires/pirelli-p-zero-pz4.png",
    tagline: "O pneu de Ferrari, Lamborghini e McLaren.",
    why: "O pneu mais escolhido pelas marcas de super-desportivos. Aderência absoluta e sensação de direção sem rival — para automóveis com mais de 250 cv.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 1 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 1 : 0) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 1 : 0) +
      (a.km === "low" ? 2 : a.km === "mid" ? 2 : 1),
  },

  // ─────────── MICHELIN ───────────
  {
    id: "michelin-primacy-5", brand: "Michelin", model: "Primacy 5",
    image: "/tires/michelin-primacy-5.png",
    tagline: "Referência absoluta em silêncio, segurança e longevidade.",
    why: "O pneu de turismo mais seguro em piso molhado da sua categoria, com a maior durabilidade do mercado. Se faz muitos km e quer paz absoluta dentro do habitáculo, é a escolha óbvia.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 4 : 1) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 3 : 2) +
      (a.estilo === "silencio" ? 5 : a.estilo === "equilibrio" ? 3 : 1) +
      (a.km === "high" ? 4 : a.km === "mid" ? 3 : 1),
  },
  {
    id: "michelin-pilot-sport-5", brand: "Michelin", model: "Pilot Sport 5",
    image: "/tires/michelin-pilot-sport-5.png",
    tagline: "O desportivo de referência da Michelin.",
    why: "Equipamento de origem em modelos Mercedes-AMG, BMW M e Porsche. Travagem em seco e aderência em curva de elite, sem perder o conforto Michelin.",
    score: (a) =>
      (a.orcamento === "performance" ? 5 : a.orcamento === "equilibrado" ? 2 : 0) +
      (a.tipo === "estrada" ? 4 : a.tipo === "misto" ? 3 : 1) +
      (a.estilo === "desporto" ? 5 : a.estilo === "equilibrio" ? 3 : 1) +
      (a.km === "mid" ? 3 : a.km === "high" ? 3 : 1),
  },
];

/** Returns the top N matches, deduplicated by brand for diversity. */
function findTopMatches(answers, n = 3) {
  // Normalize granular km buckets ("very-low"/"very-high") into the 3 internal
  // buckets ("low"/"mid"/"high") used by the per-tire score functions.
  const normalizedAnswers = {
    ...answers,
    km:
      answers.km === "very-low"
        ? "low"
        : answers.km === "very-high"
        ? "high"
        : answers.km,
  };

  // Brand boost: Hankook is the workshop's main partner brand, so we give it
  // a small score bump (+2) to slightly bias recommendations without
  // overriding strong matches from other brands.
  const BRAND_BOOST = { Hankook: 2 };

  // Car-brand → tier preference bonus (primary tier +3, secondary +1).
  const preferredTiers = CAR_PREFERENCE[answers.carro] || [];
  const tierBonus = (tireId) => {
    const tier = TIRE_TIER[tireId];
    if (!tier) return 0;
    if (preferredTiers[0] === tier) return 3;
    if (preferredTiers[1] === tier) return 1;
    return 0;
  };

  // Workshop's recommended Hankook model for the user's car segment gets a
  // small extra boost (+2) so it reliably surfaces in the top picks.
  const hankookPick = HANKOOK_PICK[answers.carro];
  const pickBonus = (tireId) => (tireId === hankookPick ? 2 : 0);

  const scored = TIRE_POOL.map((t) => ({
    tire: t,
    score:
      t.score(normalizedAnswers) +
      (BRAND_BOOST[t.brand] || 0) +
      tierBonus(t.id) +
      pickBonus(t.id),
  })).sort((a, b) => b.score - a.score);

  // Dedupe by brand so the result list is diverse.
  const seenBrands = new Set();
  const picks = [];
  for (const entry of scored) {
    if (!seenBrands.has(entry.tire.brand)) {
      seenBrands.add(entry.tire.brand);
      picks.push(entry);
      if (picks.length >= n) break;
    }
  }
  // Fallback: if we somehow don't have N picks, fill with remaining top.
  if (picks.length < n) {
    for (const entry of scored) {
      if (!picks.includes(entry)) {
        picks.push(entry);
        if (picks.length >= n) break;
      }
    }
  }
  return picks;
}

const StepProgress = ({ step, total, onJumpTo }) => (
  <div className="flex items-center gap-1.5 mb-6">
    {Array.from({ length: total }).map((_, i) => {
      const isCompleted = i < step;
      const isCurrent = i === step;
      const isClickable = isCompleted && typeof onJumpTo === "function";
      const base = `h-1 flex-1 transition-all duration-500 ${
        isCompleted ? "bg-[#FF5A00]" : isCurrent ? "bg-[#FF5A00]/60" : "bg-[#2F568F]"
      }`;
      if (isClickable) {
        return (
          <button
            key={i}
            type="button"
            data-testid={`quiz-progress-step-${i}`}
            onClick={() => onJumpTo(i)}
            aria-label={`Voltar à pergunta ${i + 1}`}
            className={`${base} cursor-pointer hover:h-2 hover:bg-white focus:outline-none`}
          />
        );
      }
      return <div key={i} className={base} aria-hidden="true" />;
    })}
  </div>
);

/** Loading state shown after the last answer — communicates "real work". */
const CalculatingState = ({ compact }) => {
  const titleSize = compact ? "text-lg md:text-xl" : "text-2xl md:text-3xl";
  const STEPS = [
    "A analisar o seu perfil de condução…",
    "A consultar a IA especialista em pneus…",
    "A escolher as melhores opções para si…",
  ];
  return (
    <div
      data-testid="quiz-calculating"
      className="flex-1 flex flex-col items-center justify-center text-center py-12"
      style={{ animation: "quizFadeIn 300ms ease-out" }}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] font-display font-bold mb-4">
        <Sparkles className="w-3.5 h-3.5 animate-pulse" />
        A processar
      </div>

      <h3 className={`font-display font-bold text-white ${titleSize} uppercase tracking-tight mb-8 max-w-xs leading-tight`}>
        A calcular as melhores
        <br />
        <span className="text-[#FF5A00]">opções para o seu perfil.</span>
      </h3>

      {/* Progress bar */}
      <div className="w-full max-w-sm h-1 bg-[#2F568F] overflow-hidden mb-6">
        <div
          className="h-full bg-[#FF5A00]"
          style={{ animation: "quizProgress 1700ms cubic-bezier(0.4, 0, 0.2, 1) forwards" }}
        />
      </div>

      {/* Step messages — fade in/out */}
      <div className="relative h-5 w-full max-w-xs">
        {STEPS.map((msg, i) => (
          <div
            key={msg}
            className="absolute inset-0 text-[#A1A1AA] font-body text-xs md:text-sm"
            style={{ animation: `quizStepMsg 1700ms ${i * 567}ms forwards` }}
          >
            {msg}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes quizProgress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes quizStepMsg {
          0%   { opacity: 0; transform: translateY(4px); }
          15%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
};

/** Inner quiz body — used by both standalone and compact versions. */
const QuizBody = ({ compact, step, answers, isDone, calculating, revealed, match, choose, back, jumpTo, restart }) => {
  if (calculating) {
    return <CalculatingState compact={compact} />;
  }

  if (!isDone) {
    const cardPad = compact ? "p-3" : "p-4 md:p-5";
    const iconBox = compact ? "w-9 h-9" : "w-11 h-11";
    const titleSize = compact ? "text-lg md:text-xl" : "text-2xl md:text-3xl";
    const optTitleSize = compact ? "text-sm" : "text-base";
    const optSubSize = compact ? "text-[11px]" : "text-xs";

    return (
      <>
        <StepProgress step={step} total={QUESTIONS.length} onJumpTo={jumpTo} />

        <div className="mb-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] font-display">
            Pergunta {step + 1} de {QUESTIONS.length}
          </span>
        </div>

        <h3
          key={`q-${step}`}
          data-testid={`question-${QUESTIONS[step].id}`}
          className={`font-display font-bold uppercase text-white ${titleSize} tracking-tight mb-6 leading-tight`}
          style={{ animation: "quizFadeIn 400ms ease-out" }}
        >
          {QUESTIONS[step].label}
        </h3>

        <div className="flex-1 grid grid-cols-1 gap-2.5">
          {QUESTIONS[step].options.map((opt, idx) => {
            const Icon = opt.icon;
            const selected = answers[QUESTIONS[step].id] === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                data-testid={`quiz-option-${QUESTIONS[step].id}-${opt.value}`}
                onClick={() => choose(QUESTIONS[step].id, opt.value)}
                className={`group flex items-center gap-3 ${cardPad} border text-left transition-all duration-200 ${
                  selected
                    ? "bg-[#FF5A00] border-[#FF5A00] text-black"
                    : "bg-[#0E2452]/40 border-[#2F568F] text-white hover:border-[#FF5A00] hover:bg-[#0E2452]"
                }`}
                style={{
                  animation: `quizSlideIn 400ms ${idx * 60}ms backwards ease-out`,
                }}
              >
                <span
                  className={`flex-shrink-0 ${iconBox} flex items-center justify-center border ${
                    selected
                      ? "bg-black border-black text-[#FF5A00]"
                      : "bg-[#0E2452] border-[#2F568F] text-[#FF5A00] group-hover:border-[#FF5A00]"
                  }`}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`font-display font-bold uppercase tracking-wider ${optTitleSize} leading-tight`}>
                    {opt.label}
                  </div>
                  <div
                    className={`font-body ${optSubSize} mt-0.5 leading-tight ${
                      selected ? "text-black/70" : "text-[#A1A1AA]"
                    }`}
                  >
                    {opt.sub}
                  </div>
                </div>
                <ChevronRight
                  className={`w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-1 ${
                    selected ? "text-black" : "text-[#5A7AAE]"
                  }`}
                />
              </button>
            );
          })}
        </div>

        {step > 0 && (
          <button
            type="button"
            data-testid="quiz-back-btn"
            onClick={back}
            className="mt-5 inline-flex items-center gap-2 px-3 py-2 border border-[#2F568F] text-white hover:border-[#FF5A00] hover:text-[#FF5A00] font-display text-[11px] uppercase tracking-widest transition-colors self-start group"
          >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            Alterar resposta anterior
          </button>
        )}
      </>
    );
  }

  // Result reveal — show all top matches as equal options, no single highlight
  const resultTitleSize = compact ? "text-lg md:text-xl" : "text-2xl md:text-3xl";
  const matches = match || [];

  return (
    <div
      data-testid="quiz-result"
      className="flex-1 flex flex-col"
      style={{ animation: "quizFadeIn 500ms ease-out" }}
    >
      <div className="text-center mb-6">
        <div
          className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] font-display font-bold mb-2 transition-all duration-500 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
          }`}
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          Match Encontrado
        </div>
        <h3
          className={`font-display font-bold text-white ${resultTitleSize} uppercase tracking-tight transition-all duration-700 ${
            revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
          }`}
        >
          As melhores opções
          <br />
          <span className="text-[#FF5A00]">para si.</span>
        </h3>
      </div>

      {/* All matches — equal cards side-by-side */}
      <div
        data-testid="quiz-options-grid"
        className={`grid grid-cols-3 gap-2 md:gap-3 transition-all duration-700 delay-200 ${
          revealed ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        {matches.map((m, idx) => {
          const { tire, reason, source } = m;
          const isAspirational = source === "aspirational";
          return (
            <div
              key={tire.id}
              data-testid={`quiz-option-card-${tire.id}`}
              className="relative flex flex-col bg-[#0E2452]/50 border border-[#2F568F] hover:border-[#FF5A00] transition-colors p-2.5 md:p-4"
              style={{
                animation: `quizSlideIn 400ms ${idx * 100}ms backwards ease-out`,
              }}
            >
              <div className="aspect-square w-full flex items-center justify-center mb-2 md:mb-3">
                {isAspirational ? (
                  <AspirationalImage brand={tire.brand} model={tire.model} />
                ) : (
                  <TireImage
                    src={tire.image}
                    alt={`${tire.brand} ${tire.model}`}
                    className="w-full h-full flex items-center justify-center"
                    imgClassName="max-h-20 md:max-h-32 max-w-full object-contain drop-shadow-[0_15px_30px_rgba(0,0,0,0.5)]"
                    skeletonShape="rect"
                  />
                )}
              </div>
              <div className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.25em] text-[#FF5A00] font-display font-bold">
                {tire.brand}
              </div>
              <div className="font-display font-bold uppercase text-white text-xs md:text-sm leading-tight mt-1 mb-1.5 md:mb-2">
                {tire.model}
              </div>
              <p className="text-[#A1A1AA] font-body text-[10px] md:text-[11px] leading-snug md:leading-relaxed line-clamp-4 flex-1">
                {reason || tire.tagline}
              </p>
            </div>
          );
        })}
      </div>

      <div
        className={`flex flex-col sm:flex-row gap-2 mt-6 ${compact ? "" : "justify-center"} transition-all duration-700 delay-500 ${
          revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
        }`}
      >
        <a
          href="/orcamento"
          data-testid="quiz-cta-orcamento"
          className={`btn-primary ${compact ? "flex-1 text-xs py-3 px-4" : "text-sm py-3 px-6"}`}
        >
          Pedir orçamento
          <ChevronRight className="w-3.5 h-3.5" />
        </a>
        <button
          type="button"
          data-testid="quiz-restart-btn"
          onClick={restart}
          className={`${compact ? "flex-1" : ""} inline-flex items-center justify-center gap-2 px-4 py-3 bg-transparent border border-[#2F568F] text-white hover:border-[#FF5A00] hover:text-[#FF5A00] font-display font-bold uppercase tracking-wider text-xs transition-colors`}
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Repetir
        </button>
      </div>
    </div>
  );
};

const QUIZ_STYLE = (
  <style>{`
    @keyframes quizFadeIn {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes quizSlideIn {
      from { opacity: 0; transform: translateX(-12px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `}</style>
);

export const TinderPneus = ({ compact = false }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [calculating, setCalculating] = useState(false);
  // AI response: { catalog: [{tire_id, reason}, ...], aspirational: {brand, model, reason} }
  const [aiResult, setAiResult] = useState(null);

  const isLastQuestion = step === QUESTIONS.length - 1;
  const isDone = step >= QUESTIONS.length;

  // Local fallback recommendation (used when AI fails/times out)
  const localMatch = useMemo(() => (isDone ? findTopMatches(answers, 3) : null), [isDone, answers]);

  // Build the display shape from AI result + fall back to localMatch
  const match = useMemo(() => {
    if (aiResult && aiResult.catalog) {
      const catalogItems = aiResult.catalog
        .map((c) => {
          const tire = TIRE_POOL.find((t) => t.id === c.tire_id);
          return tire ? { tire, reason: c.reason, source: "catalog" } : null;
        })
        .filter(Boolean);
      const asp = aiResult.aspirational
        ? {
            tire: {
              id: `aspirational-${aiResult.aspirational.model}`,
              brand: aiResult.aspirational.brand,
              model: aiResult.aspirational.model,
              image: aiResult.aspirational.image_url || null,
              tagline: aiResult.aspirational.reason,
            },
            reason: aiResult.aspirational.reason,
            source: "aspirational",
          }
        : null;
      return asp ? [...catalogItems, asp] : catalogItems;
    }
    return localMatch;
  }, [aiResult, localMatch]);

  const choose = (questionId, value) => {
    const next = { ...answers, [questionId]: value };
    setAnswers(next);
    if (isLastQuestion) {
      // Call the AI backend — with local algorithm as fallback if it fails.
      setCalculating(true);
      setAiResult(null);
      const startedAt = Date.now();
      axios
        .post(`${API}/quiz/recommend`, next, { timeout: 18000 })
        .then((r) => setAiResult(r.data))
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("AI quiz failed, using local algorithm fallback:", err?.message);
          setAiResult(null); // triggers localMatch fallback via useMemo
        })
        .finally(() => {
          // Ensure the "calculating" screen shows for at least 1.4s so the
          // user perceives real work being done even if AI is very fast.
          const elapsed = Date.now() - startedAt;
          const wait = Math.max(0, 1400 - elapsed);
          setTimeout(() => {
            setCalculating(false);
            setStep((s) => s + 1);
            setTimeout(() => setRevealed(true), 150);
          }, wait);
        });
    } else {
      setStep(step + 1);
    }
  };

  const back = () => {
    if (step > 0) {
      setStep(step - 1);
      setRevealed(false);
    }
  };

  const jumpTo = (targetStep) => {
    if (targetStep >= 0 && targetStep < step) {
      setStep(targetStep);
      setRevealed(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setRevealed(false);
    setCalculating(false);
    setAiResult(null);
  };

  const bodyProps = { compact, step, answers, isDone, calculating, revealed, match, choose, back, jumpTo, restart };

  if (compact) {
    return (
      <div
        data-testid="tinder-pneus-compact"
        className="bg-[#163A6D] border border-[#2F568F] p-5 md:p-6 flex flex-col h-full rounded-2xl"
      >
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] font-display font-bold">
            <Sparkles className="w-3.5 h-3.5" />
            Match em 30 segundos
          </span>
          <h3 className="font-display font-bold text-white text-2xl md:text-3xl uppercase tracking-tight mt-3 leading-tight">
            Encontre o seu
            <br />
            <span className="text-[#FF5A00]">pneu ideal.</span>
          </h3>
        </div>

        <div className="flex-1 flex flex-col">
          <QuizBody {...bodyProps} />
        </div>

        {QUIZ_STYLE}
      </div>
    );
  }

  return (
    <section
      data-testid="tinder-pneus"
      id="match-pneu"
      className="relative py-14 md:py-16 bg-[#0B1D44] border-t border-[#2F568F] overflow-hidden"
    >
      <div className="relative w-full px-6 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="section-label inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              // Match Perfeito em 30 segundos
            </span>
            <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mt-3 leading-[0.95]">
              Encontre o pneu
              <br />
              <span className="text-[#FF5A00]">feito para si.</span>
            </h2>
            <p className="text-[#A1A1AA] font-body text-base md:text-lg max-w-xl mx-auto mt-5">
              Responda a 4 perguntas rápidas e descubra qual o pneu que melhor
              se adapta ao seu estilo de condução, orçamento e quilometragem.
            </p>
          </div>

          <div
            data-testid="quiz-card"
            className="bg-[#163A6D] border border-[#2F568F] p-6 md:p-10 min-h-[480px] flex flex-col"
          >
            <QuizBody {...bodyProps} />
          </div>

          <p className="text-[11px] text-[#5A7AAE] font-body mt-6 leading-relaxed text-center">
            * As recomendações são indicativas. Para uma escolha final, traga o
            livrete à oficina — confirmamos a medida exata e a melhor opção em
            stock.
          </p>
        </div>
      </div>

      {QUIZ_STYLE}
    </section>
  );
};

export default TinderPneus;
